import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLazyQuery, useMutation } from '@apollo/client';
import EditInfoModal from '../../../../components/EditInfoModal';
import {
  IDashboardCloneMutation,
  IDashboardCloneMutationVariables,
  IDashboardInstanceFieldsFragment,
  IDashboardTemplateFieldsFragment,
  IGetDashboardInstanceQuery,
  IGetDashboardInstanceQueryVariables,
} from '../../../../utils/apollo/graphqlSchema';
import {
  CLONE_DASHBOARD,
  GET_DASHBOARD_INSTANCE,
} from '../../../../utils/apollo/dashboards';
import { assertNotNil } from '../../../../utils/types';
import printGraphqlErrors from '../../../../utils/apollo/printGraphqlErrors';
import { closeModal } from '../../../../redux/slices/modal';
import { appRoutes } from '../../../../path';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { selectModalData } from '../../../../redux/selectors/modal';
import toastMessage from '../../../../utils/toastMessage';
import { reloadDashboardTemplates } from '../../../../redux/slices/dashboards/dashboardInstance';

const DashboardDuplicateModal = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const data:
    | {
        type: 'template';
        template: IDashboardTemplateFieldsFragment;
      }
    | {
        type: 'instance';
        instance: IDashboardInstanceFieldsFragment;
      } = useAppSelector(selectModalData);
  assertNotNil(data);
  const { name, description } =
    data.type === 'template' ? data.template : data.instance;

  const handleClose = () => {
    dispatch(closeModal());
  };

  const [getDashboardInstance] = useLazyQuery<
    IGetDashboardInstanceQuery,
    IGetDashboardInstanceQueryVariables
  >(GET_DASHBOARD_INSTANCE);

  const handleCompleteMutation = ({
    dashboardClone,
  }: IDashboardCloneMutation) => {
    const { errors, entity } = dashboardClone;

    if (printGraphqlErrors(errors)) {
      return;
    }

    assertNotNil(entity);

    dispatch(reloadDashboardTemplates());

    // Navigate to a new dashboard
    navigate(appRoutes.dashboard.generateLink(entity.baseId));

    toastMessage({
      message: `Dashboard ${name} has been duplicated`,
      type: 'info',
    });

    handleClose();
  };
  const [cloneDashboard, { loading }] = useMutation<
    IDashboardCloneMutation,
    IDashboardCloneMutationVariables
  >(CLONE_DASHBOARD, {
    onCompleted: handleCompleteMutation,
  });

  const handleSubmit = async (newName: string, newDescription: string) => {
    // If we want to duplicate a template (from DashboardNav) - first we need
    // to get a dashboard instance and then clone it
    if (data.type === 'template') {
      const instanceData = await getDashboardInstance({
        variables: {
          baseId: data.template.baseId,
        },
      });

      const instanceId = instanceData?.data?.dashboardFromTemplate?.id;
      assertNotNil(instanceId);

      cloneDashboard({
        variables: {
          input: {
            name: newName,
            description: newDescription,
            instanceId,
          },
        },
      });
    } else {
      cloneDashboard({
        variables: {
          input: {
            name: newName,
            description: newDescription,
            instanceId: data.instance.id,
          },
        },
      });
    }
  };

  return (
    <EditInfoModal
      entity="duplicateDashboard"
      name={`${name} 1`}
      description={description}
      onClose={handleClose}
      onSubmit={handleSubmit}
      entitiesList={[]}
      isLoading={loading}
      submitLabel={'Duplicate & Go'}
      trackContext="dashboards"
      trackSubmitAction="duplicate - duplicate & go"
      trackCancelAction="duplicate - cancel"
    />
  );
};

export default DashboardDuplicateModal;
